import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { exportElementToPDF } from "@/lib/pdfExport";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  Clock,
  User,
  Check,
  X,
  AlertCircle,
  Plus,
  Pencil,
  Users,
  ChevronsUpDown,
  Search,
  FileDown,
} from "lucide-react";
import { cn } from "@/lib/utils";

const API = "/api";

// Time slots from 6:00 AM to 11:30 PM in 30-minute increments (more reasonable hours)
const TIME_SLOTS = [];
for (let hour = 6; hour < 24; hour++) {
  for (let min = 0; min < 60; min += 30) {
    const h = hour.toString().padStart(2, "0");
    const m = min.toString().padStart(2, "0");
    TIME_SLOTS.push(`${h}:${m}`);
  }
}

// Format time for display (12h format)
function formatTime12h(time24) {
  const [hours, minutes] = time24.split(":");
  const h = parseInt(hours);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${minutes} ${ampm}`;
}

// Check if a slot is a trial class based on student name
function isTrial(slot) {
  if (!slot?.student_name) return false;
  return /trial/i.test(slot.student_name);
}

// Get status color and label
function getStatusStyle(status, slot) {
  const trial = isTrial(slot);
  switch (status) {
    case "available":
      return { bg: "bg-blue-500", text: "text-white", label: "Available" };
    case "booked":
      return trial
        ? { bg: "bg-pink-200", text: "text-pink-900", label: "Trial" }
        : { bg: "bg-emerald-500", text: "text-white", label: "Booked" };
    case "present":
      return trial
        ? { bg: "bg-pink-300", text: "text-pink-900", label: "Present" }
        : { bg: "bg-purple-400", text: "text-white", label: "Present" };
    case "no_show":
      return { bg: "bg-amber-400", text: "text-amber-900", label: "No Show" };
    case "cancelled":
      return { bg: "bg-red-300", text: "text-red-900", label: "Cancelled" };
    case "cancelled_by_student":
      return { bg: "bg-orange-400", text: "text-orange-900", label: "Cancelled by Student" };
    case "cancelled_by_teacher":
      return { bg: "bg-red-400", text: "text-red-900", label: "Cancelled by Teacher" };
    default:
      return { bg: "bg-slate-200", text: "text-slate-700", label: status };
  }
}

// Parse date string to get day info - handles timezone correctly
function getWeekDays(startDate) {
  const days = [];
  // Parse date parts directly to avoid timezone issues
  const [year, month, day] = startDate.split("-").map(Number);
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  
  for (let i = 0; i < 7; i++) {
    // Create date using UTC to avoid timezone shifts
    const date = new Date(Date.UTC(year, month - 1, day + i));
    days.push({
      date: `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`,
      dayName: dayNames[date.getUTCDay()],
      dayNum: date.getUTCDate(),
      month: monthNames[date.getUTCMonth()],
      year: date.getUTCFullYear(),
    });
  }
  return days;
}

export default function Schedule() {
  const { user, getAuthHeader } = useAuth();
  const isAdmin = user?.role === "admin";
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const scheduleRef = useRef(null);

  const [schedule, setSchedule] = useState({ week_start: "", week_end: "", slots: [] });
  const [weekDays, setWeekDays] = useState([]);
  const [students, setStudents] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [selectedTutor, setSelectedTutor] = useState("");
  const [highlightSlotId, setHighlightSlotId] = useState("");
  const [initialNavApplied, setInitialNavApplied] = useState(false);
  
  // Dialog states
  const [assignDialog, setAssignDialog] = useState({ open: false, slot: null });
  const [tagDialog, setTagDialog] = useState({ open: false, slot: null });
  const [studentName, setStudentName] = useState("");
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [studentSearchOpen, setStudentSearchOpen] = useState(false);
  const [tutorSearchOpen, setTutorSearchOpen] = useState(false);
  const [tagNotes, setTagNotes] = useState("");
  const [editMode, setEditMode] = useState(false); // For admin to edit booking

  // Fetch schedule data
  const fetchSchedule = useCallback(async (weekOf = null, { silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const params = weekOf ? { week_of: weekOf } : {};
      params._ts = Date.now(); // never serve the schedule from browser cache
      
      let response;
      if (isAdmin) {
        // Admin viewing schedules
        if (selectedTutor) {
          params.tutor_id = selectedTutor;
        }
        response = await axios.get(`${API}/schedule/all`, {
          headers: getAuthHeader(),
          params,
        });
      } else {
        // Tutor viewing own schedule
        response = await axios.get(`${API}/schedule/my-schedule`, {
          headers: getAuthHeader(),
          params,
        });
      }
      
      setSchedule(response.data);
      setWeekDays(getWeekDays(response.data.week_start));
    } catch (error) {
      console.error("Failed to fetch schedule:", error);
      if (!silent) toast.error("Failed to load schedule");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [getAuthHeader, isAdmin, selectedTutor]);

  // Fetch students list
  const fetchStudents = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/schedule/students-list`, {
        headers: getAuthHeader(),
      });
      setStudents(response.data || []);
    } catch (error) {
      console.error("Failed to fetch students:", error);
      setStudents([]);
    }
  }, [getAuthHeader]);

  // Fetch tutors list (admin only)
  const fetchTutors = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const response = await axios.get(`${API}/schedule/tutors-list`, {
        headers: getAuthHeader(),
      });
      setTutors(response.data || []);
    } catch (error) {
      console.error("Failed to fetch tutors:", error);
      setTutors([]);
    }
  }, [getAuthHeader, isAdmin]);

  useEffect(() => {
    fetchStudents();
    fetchTutors();
  }, [fetchStudents, fetchTutors]);

  // Apply URL search params on mount: ?tutor_id=...&week_of=YYYY-MM-DD&highlight=slot_id
  // Lets the Untagged Class Alerts page deep-link straight into the right tutor's week.
  // The deep-link feature is admin-only — non-admin users skip this and immediately release
  // the gate so the default-fetch effect can call /api/schedule/my-schedule.
  useEffect(() => {
    if (initialNavApplied) return;
    if (!isAdmin) {
      setInitialNavApplied(true);
      return;
    }
    const urlTutor = searchParams.get("tutor_id");
    const urlWeek = searchParams.get("week_of");
    const urlHighlight = searchParams.get("highlight");
    if (urlTutor) setSelectedTutor(urlTutor);
    if (urlHighlight) setHighlightSlotId(urlHighlight);
    if (urlTutor || urlWeek) {
      // Force one targeted fetch — selectedTutor state update is async, so pass tutor explicitly
      const params = {};
      if (urlWeek) params.week_of = urlWeek;
      if (urlTutor) params.tutor_id = urlTutor;
      setLoading(true);
      axios
        .get(`${API}/schedule/all`, { headers: getAuthHeader(), params })
        .then((response) => {
          setSchedule(response.data);
          const days = getWeekDays(response.data.week_start);
          setWeekDays(days);
        })
        .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load schedule"))
        .finally(() => {
          setLoading(false);
          setInitialNavApplied(true);
        });
    } else {
      setInitialNavApplied(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  // Scroll highlighted slot into view + auto-clear after 5s so the glow doesn't linger forever
  useEffect(() => {
    if (!highlightSlotId || !schedule.slots?.length) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-slot-id="${highlightSlotId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 300);
    const clear = setTimeout(() => {
      setHighlightSlotId("");
      const next = new URLSearchParams(searchParams);
      next.delete("highlight");
      setSearchParams(next, { replace: true });
    }, 8000);
    return () => {
      clearTimeout(t);
      clearTimeout(clear);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightSlotId, schedule.slots]);

  useEffect(() => {
    // Default fetch only runs after initial deep-link nav (or when there are no URL params)
    if (!initialNavApplied) return;
    // The "skip when URL params present" guard is admin-only — tutors must always fetch
    // /api/schedule/my-schedule regardless of any deep-link params in the URL.
    if (isAdmin && (searchParams.get("tutor_id") || searchParams.get("week_of"))) return;
    fetchSchedule();
  }, [fetchSchedule, initialNavApplied, searchParams, isAdmin]);

  // Auto-refresh the week being viewed so student reschedules/bookings show up
  // without a manual reload (on window focus, when the tab becomes visible, and every 60s).
  // Skipped for admins on a deep-linked tutor view, which is loaded by the URL effect above.
  const weekStartRef = useRef("");
  weekStartRef.current = schedule.week_start;
  useEffect(() => {
    if (!initialNavApplied) return;
    const refresh = () => {
      if (document.visibilityState !== "visible" || !weekStartRef.current) return;
      if (isAdmin && !selectedTutor && searchParams.get("tutor_id")) return;
      fetchSchedule(weekStartRef.current, { silent: true });
    };
    const onVisible = () => { if (document.visibilityState === "visible") refresh(); };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", onVisible);
    const id = setInterval(refresh, 60000);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(id);
    };
  }, [fetchSchedule, initialNavApplied, isAdmin, selectedTutor, searchParams]);

  // Navigate weeks - using UTC to avoid timezone issues
  const goToPreviousWeek = () => {
    const [year, month, day] = schedule.week_start.split("-").map(Number);
    const prevWeek = new Date(Date.UTC(year, month - 1, day - 7));
    const dateStr = `${prevWeek.getUTCFullYear()}-${String(prevWeek.getUTCMonth() + 1).padStart(2, '0')}-${String(prevWeek.getUTCDate()).padStart(2, '0')}`;
    fetchSchedule(dateStr);
  };

  const goToNextWeek = () => {
    const [year, month, day] = schedule.week_start.split("-").map(Number);
    const nextWeek = new Date(Date.UTC(year, month - 1, day + 7));
    const dateStr = `${nextWeek.getUTCFullYear()}-${String(nextWeek.getUTCMonth() + 1).padStart(2, '0')}-${String(nextWeek.getUTCDate()).padStart(2, '0')}`;
    fetchSchedule(dateStr);
  };

  const goToToday = () => {
    fetchSchedule();
  };

  // Get slot for specific date and time
  // If several slots share a cell (e.g. an "available" placeholder plus a booked class),
  // show the highlighted one first, then any real class, and "available" only as a last resort.
  const getSlot = (date, time) => {
    const matches = schedule.slots.filter((s) => s.date === date && s.time === time);
    if (matches.length <= 1) return matches[0];
    return (
      matches.find((s) => highlightSlotId && s.id === highlightSlotId) ||
      matches.find((s) => s.status === "booked") ||
      matches.find((s) => s.status !== "available") ||
      matches[0]
    );
  };

  // Grid rows: the standard 06:00-23:30 rows plus any other time a class actually has,
  // so classes at off-grid times (e.g. 00:00 or 10:15) are never hidden.
  const gridTimes = Array.from(
    new Set([...TIME_SLOTS, ...(schedule.slots || []).map((s) => s.time).filter(Boolean)])
  ).sort();

  // Create slot
  const createSlot = async (date, time) => {
    try {
      if (isAdmin) {
        if (!selectedTutor) {
          toast.error("Please select a tutor first");
          return;
        }
        await axios.post(
          `${API}/schedule/admin/slots`,
          { tutor_id: selectedTutor, date, time },
          { headers: getAuthHeader() }
        );
      } else {
        await axios.post(
          `${API}/schedule/slots`,
          { date, time },
          { headers: getAuthHeader() }
        );
      }
      toast.success("Slot created");
      fetchSchedule(schedule.week_start);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create slot");
    }
  };

  // Delete slot
  const deleteSlot = async (slotId) => {
    try {
      if (isAdmin) {
        await axios.delete(`${API}/schedule/admin/slots/${slotId}`, {
          headers: getAuthHeader(),
        });
      } else {
        await axios.delete(`${API}/schedule/slots/${slotId}`, {
          headers: getAuthHeader(),
        });
      }
      toast.success("Slot deleted");
      fetchSchedule(schedule.week_start);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete slot");
    }
  };

  // Open assign dialog
  const openAssignDialog = (slot) => {
    setStudentName(slot.student_name || "");
    setSelectedStudentId(slot.student_id || "");
    setAssignDialog({ open: true, slot });
  };

  // Handle student selection from dropdown
  const handleStudentSelect = (studentId) => {
    if (studentId === "manual") {
      setSelectedStudentId("");
      return;
    }
    setSelectedStudentId(studentId);
    const student = students.find(s => s.id === studentId);
    if (student) {
      setStudentName(student.name);
    }
  };

  // Assign student to slot
  const handleAssignStudent = async () => {
    if (!assignDialog.slot || !studentName.trim()) {
      toast.error("Please enter or select a student name");
      return;
    }
    
    try {
      if (isAdmin) {
        await axios.put(
          `${API}/schedule/admin/slots/${assignDialog.slot.id}`,
          { 
            student_name: studentName.trim(),
            student_id: selectedStudentId || null,
            status: "booked"
          },
          { headers: getAuthHeader() }
        );
      } else {
        await axios.put(
          `${API}/schedule/slots/${assignDialog.slot.id}/assign`,
          { 
            student_name: studentName.trim(),
            student_id: selectedStudentId || null
          },
          { headers: getAuthHeader() }
        );
      }
      toast.success("Student assigned to class");
      setAssignDialog({ open: false, slot: null });
      setStudentName("");
      setSelectedStudentId("");
      fetchSchedule(schedule.week_start);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign student");
    }
  };

  // Unassign student from slot
  const handleUnassignStudent = async () => {
    if (!assignDialog.slot) return;
    
    try {
      if (isAdmin) {
        await axios.put(
          `${API}/schedule/admin/slots/${assignDialog.slot.id}`,
          { student_name: "", student_id: null, status: "available" },
          { headers: getAuthHeader() }
        );
      } else {
        await axios.put(
          `${API}/schedule/slots/${assignDialog.slot.id}/unassign`,
          {},
          { headers: getAuthHeader() }
        );
      }
      toast.success("Student removed from class");
      setAssignDialog({ open: false, slot: null });
      setStudentName("");
      setSelectedStudentId("");
      fetchSchedule(schedule.week_start);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to unassign student");
    }
  };

  // Quick unassign - directly from slot X button
  const handleQuickUnassign = async (slot) => {
    if (!slot) return;
    
    // Confirm before removing
    if (!window.confirm(`Remove "${slot.student_name}" from this slot and make it available again?`)) {
      return;
    }
    
    try {
      if (isAdmin) {
        await axios.put(
          `${API}/schedule/admin/slots/${slot.id}`,
          { student_name: "", student_id: null, status: "available" },
          { headers: getAuthHeader() }
        );
      } else {
        await axios.put(
          `${API}/schedule/slots/${slot.id}/unassign`,
          {},
          { headers: getAuthHeader() }
        );
      }
      toast.success("Student removed - slot is now available");
      fetchSchedule(schedule.week_start);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove student");
    }
  };

  // Open tag dialog
  const openTagDialog = (slot) => {
    setSelectedStatus(slot.status);
    setTagNotes(slot.notes || "");
    setEditMode(false); // Reset edit mode
    setStudentName(slot.student_name || "");
    setSelectedStudentId(slot.student_id || "");
    setTagDialog({ open: true, slot });
  };

  // Tag slot (Present, No Show, Cancelled)
  const handleTagSlot = async () => {
    if (!tagDialog.slot || !selectedStatus) return;
    
    try {
      // Always use the main tag endpoint for status changes (handles class records + credits)
      const response = await axios.put(
        `${API}/schedule/slots/${tagDialog.slot.id}/tag`,
        { status: selectedStatus, notes: tagNotes },
        { headers: getAuthHeader() }
      );
      
      if (response.data.credit_message) {
        toast.success(response.data.credit_message);
      } else {
        toast.success("Class status updated");
      }
      
      setTagDialog({ open: false, slot: null });
      setSelectedStatus("");
      setTagNotes("");
      setEditMode(false);
      fetchSchedule(schedule.week_start);
      fetchStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update class status");
    }
  };

  // Handle edit booking (Admin only) - change student assignment
  const handleEditBooking = async () => {
    if (!tagDialog.slot) return;
    
    // Determine new student ID and name
    const newStudentId = selectedStudentId || null;
    const newStudentName = selectedStudentId 
      ? students.find(s => s.id === selectedStudentId)?.name || studentName
      : studentName;
    
    try {
      await axios.put(
        `${API}/schedule/admin/slots/${tagDialog.slot.id}`,
        { 
          student_id: newStudentId,
          student_name: newStudentName,
          status: tagDialog.slot.status === "available" ? "booked" : tagDialog.slot.status,
          notes: tagNotes
        },
        { headers: getAuthHeader() }
      );
      
      toast.success("Booking updated successfully");
      setTagDialog({ open: false, slot: null });
      setEditMode(false);
      setStudentName("");
      setSelectedStudentId("");
      setTagNotes("");
      fetchSchedule(schedule.week_start);
      fetchStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update booking");
    }
  };

  // Handle slot click
  const handleSlotClick = (slot) => {
    const cancelledStatuses = ["cancelled", "cancelled_by_student", "cancelled_by_teacher"];
    const taggedStatuses = ["present", "no_show", ...cancelledStatuses];
    
    if (slot.status === "available") {
      // Both admin and tutor can book students into available slots
      openAssignDialog(slot);
    } else if (slot.status === "booked") {
      if (isAdmin) {
        // Admin: open tag dialog to set status or edit booking
        openTagDialog(slot);
      } else {
        // Tutor: can tag their booked slots once (Present/No Show/Cancelled)
        openTagDialog(slot);
      }
    } else if (isAdmin) {
      // Only admin can interact with already-tagged or cancelled slots
      if (cancelledStatuses.includes(slot.status)) {
        openAssignDialog(slot);
      } else {
        openTagDialog(slot);
      }
    }
    // Tutors: clicking on already-tagged slots does nothing (locked after first tag)
  };

  // Export schedule to PDF - captures the exact UI
  const handleExportPDF = async () => {
    if (!scheduleRef.current) return;
    try {
      toast.info("Generating PDF...");
      await exportElementToPDF(
        scheduleRef.current,
        `schedule_${schedule.week_start}_to_${schedule.week_end}.pdf`,
        { orientation: "landscape" }
      );
      toast.success("Schedule PDF downloaded!");
    } catch (error) {
      toast.error("Failed to export PDF");
    }
  };

  if (loading && !schedule.week_start) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="schedule-loading">
        <div className="animate-pulse text-slate-400">Loading schedule...</div>
      </div>
    );
  }

  const bookedSlots = schedule.slots.filter((s) => s.status !== "available").length;

  return (
    <div className="space-y-6" data-testid="schedule-page" ref={scheduleRef}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
            {isAdmin ? "Manage Schedules" : "My Schedule"}
          </h1>
          <p className="text-slate-500 mt-1">
            Week of {weekDays[0]?.month} {weekDays[0]?.dayNum}, {weekDays[0]?.year}
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={handleExportPDF} data-testid="export-schedule-pdf">
              <FileDown className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
          )}
          <Badge variant="outline" className="font-normal">
            <Clock className="w-3 h-3 mr-1" />
            {bookedSlots} classes booked
          </Badge>
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
            Europe/Istanbul
          </Badge>
        </div>
      </div>

      {/* Admin: Tutor Selector */}
      {isAdmin && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-5 h-5 text-indigo-600" />
            <Label className="text-indigo-800 font-medium">Search & Select Tutor to Manage</Label>
          </div>
          <Popover open={tutorSearchOpen} onOpenChange={setTutorSearchOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={tutorSearchOpen}
                className="w-full justify-between h-12 bg-white font-normal"
                data-testid="tutor-select"
              >
                {selectedTutor ? (
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-indigo-500" />
                    <span className="font-medium">{tutors.find(t => t.id === selectedTutor)?.name}</span>
                    <span className="text-slate-400 text-sm">({tutors.find(t => t.id === selectedTutor)?.email})</span>
                  </div>
                ) : (
                  <span className="text-slate-500">Search for a tutor...</span>
                )}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[400px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Type to search tutors..." />
                <CommandList>
                  <CommandEmpty>No tutor found.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      value="all-tutors-view"
                      onSelect={() => {
                        setSelectedTutor("");
                        setTutorSearchOpen(false);
                      }}
                      className="cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <Check className={cn("h-4 w-4", !selectedTutor ? "opacity-100" : "opacity-0")} />
                        <span className="text-slate-500 italic">All Tutors (View Only)</span>
                      </div>
                    </CommandItem>
                  </CommandGroup>
                  <CommandGroup heading="Tutors">
                    {tutors.map((tutor) => (
                      <CommandItem
                        key={tutor.id}
                        value={tutor.name + " " + tutor.email}
                        onSelect={() => {
                          setSelectedTutor(tutor.id);
                          setTutorSearchOpen(false);
                        }}
                        className="cursor-pointer"
                      >
                        <div className="flex items-center gap-2 w-full">
                          <Check className={cn("h-4 w-4", selectedTutor === tutor.id ? "opacity-100" : "opacity-0")} />
                          <User className="w-4 h-4 text-indigo-500" />
                          <span className="font-medium">{tutor.name}</span>
                          <span className="text-slate-400 text-sm ml-auto">({tutor.email})</span>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          {!selectedTutor && tutors.length > 0 && (
            <p className="text-xs text-indigo-600 mt-2">
              ⚠️ Select a specific tutor to create/edit their schedule. Currently viewing all schedules in read-only mode.
            </p>
          )}
          {tutors.length === 0 && (
            <p className="text-xs text-red-600 mt-2">
              No tutors found. Please add tutors from the Users page first.
            </p>
          )}
        </div>
      )}

      {/* Navigation - Sticky */}
      <div className="flex items-center justify-between sticky top-0 z-20 bg-white py-3 border-b border-slate-200">
        <Button variant="outline" onClick={goToPreviousWeek} data-testid="prev-week-btn">
          <ChevronLeft className="w-4 h-4 mr-1" />
          Previous
        </Button>
        <Button variant="outline" onClick={goToToday} data-testid="today-btn">
          Today
        </Button>
        <Button variant="outline" onClick={goToNextWeek} data-testid="next-week-btn">
          Next
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>

      {/* Schedule Grid */}
      <Card className="border-slate-200 shadow-sm overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto max-h-[calc(100vh-280px)]">
            <table className="w-full min-w-[800px]">
              <thead className="sticky top-0 z-10">
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="py-3 px-4 text-left text-sm font-semibold text-slate-600 w-24 sticky left-0 bg-slate-50 z-20">
                    Time
                  </th>
                  {weekDays.map((day) => (
                    <th
                      key={day.date}
                      className="py-3 px-2 text-center text-sm font-semibold text-slate-600 min-w-[120px] bg-slate-50"
                    >
                      <div>{day.dayName}</div>
                      <div className="text-xs font-normal text-slate-400">
                        {day.month} {day.dayNum}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {gridTimes.map((time) => (
                  <tr key={time} className="border-b border-slate-100 hover:bg-slate-50/50">
                    <td className="py-1 px-4 text-sm text-slate-600 font-medium whitespace-nowrap sticky left-0 bg-white z-10">
                      {formatTime12h(time)}
                    </td>
                    {weekDays.map((day) => {
                      const slot = getSlot(day.date, time);
                      const style = slot ? getStatusStyle(slot.status, slot) : null;
                      const canEdit = !isAdmin || selectedTutor;
                      
                      return (
                        <td key={`${day.date}-${time}`} className="py-1 px-1">
                          {slot ? (
                            <div
                              className={`rounded-md p-2 transition-all ${style.bg} ${style.text} min-h-[44px] flex flex-col justify-center ${
                                slot.status === "available" || slot.status === "booked" || isAdmin 
                                  ? "cursor-pointer hover:opacity-90" 
                                  : "cursor-default"
                              } ${
                                highlightSlotId && slot.id === highlightSlotId
                                  ? "ring-4 ring-rose-500 ring-offset-2 shadow-lg animate-pulse"
                                  : ""
                              }`}
                              onClick={() => handleSlotClick(slot)}
                              data-testid={`slot-${day.date}-${time}`}
                              data-slot-id={slot.id}
                            >
                              <div className="flex items-center justify-between gap-1">
                                <span className="text-xs font-medium truncate flex-1">
                                  {slot.student_name || style.label}
                                </span>
                                {slot.status === "available" && canEdit && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      deleteSlot(slot.id);
                                    }}
                                    className="p-0.5 hover:bg-white/20 rounded flex-shrink-0"
                                    title="Delete slot"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                )}
                                {slot.status === "booked" && isAdmin && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleQuickUnassign(slot);
                                    }}
                                    className="p-0.5 hover:bg-white/30 rounded flex-shrink-0"
                                    title="Remove student & make available"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                )}
                                {slot.status !== "available" && slot.status !== "booked" && isAdmin && (
                                  <Pencil className="w-3 h-3 opacity-60 flex-shrink-0" />
                                )}
                              </div>
                              {slot.student_name && slot.status !== "booked" && (
                                <div className="text-xs opacity-80 mt-0.5">
                                  {style.label}
                                </div>
                              )}
                              {isAdmin && slot.tutor_name && (
                                <div className="text-xs opacity-70 mt-0.5 truncate">
                                  📚 {slot.tutor_name}
                                </div>
                              )}
                              {isTrial(slot) && (
                                <span className="inline-block text-[9px] font-bold tracking-wide bg-pink-400/30 text-pink-900 px-1 py-0.5 rounded mt-0.5 leading-none">
                                  TRIAL
                                </span>
                              )}
                            </div>
                          ) : (
                            <button
                              className={`w-full h-11 rounded-md border-2 border-dashed transition-all flex items-center justify-center ${
                                canEdit 
                                  ? "border-slate-200 text-slate-400 hover:border-blue-400 hover:text-blue-500 hover:bg-blue-50/50 cursor-pointer" 
                                  : "border-slate-100 text-slate-300 cursor-not-allowed"
                              }`}
                              onClick={() => canEdit && createSlot(day.date, time)}
                              disabled={!canEdit}
                              data-testid={`empty-slot-${day.date}-${time}`}
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 justify-center text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-blue-500"></div>
          <span className="text-slate-600">Available</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-emerald-500"></div>
          <span className="text-slate-600">Booked</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-purple-400"></div>
          <span className="text-slate-600">Present</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-amber-400"></div>
          <span className="text-slate-600">No Show</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-red-300"></div>
          <span className="text-slate-600">Cancelled</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-pink-200 border border-pink-300"></div>
          <span className="text-slate-600">Trial</span>
        </div>
      </div>

      {/* Assign Student Dialog */}
      <Dialog open={assignDialog.open} onOpenChange={(open) => {
        if (!open) {
          setAssignDialog({ open: false, slot: null });
          setStudentName("");
          setSelectedStudentId("");
          setStudentSearchOpen(false);
        }
      }}>
        <DialogContent className="sm:max-w-md" data-testid="assign-dialog">
          <DialogHeader>
            <DialogTitle>
              {assignDialog.slot?.student_name ? "Edit Class Booking" : "Book Class for Student"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="text-sm text-slate-600 space-y-1 bg-slate-50 p-3 rounded-lg">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-indigo-500" />
                <span className="font-medium">{assignDialog.slot?.date}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-indigo-500" />
                <span>{assignDialog.slot?.time && formatTime12h(assignDialog.slot.time)} (Istanbul)</span>
              </div>
            </div>
            
            {/* Searchable Student Selector (PRIMARY) */}
            <div className="space-y-2">
              <Label className="font-medium flex items-center gap-2">
                <Search className="w-4 h-4" />
                Search & Select Student
              </Label>
              <Popover open={studentSearchOpen} onOpenChange={setStudentSearchOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={studentSearchOpen}
                    className="w-full justify-between h-12 font-normal"
                    data-testid="student-search-btn"
                  >
                    {selectedStudentId ? (
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-indigo-500" />
                        <span className="font-medium">{students.find(s => s.id === selectedStudentId)?.name}</span>
                        {isAdmin && (() => {
                          const s = students.find(s => s.id === selectedStudentId);
                          const avail = s?.available_credits ?? s?.remaining_classes;
                          return (
                            <Badge
                              variant="outline"
                              className={cn(
                                "ml-auto text-xs",
                                avail === 0 ? "bg-red-50 text-red-700 border-red-200" : ""
                              )}
                            >
                              {avail} available
                            </Badge>
                          );
                        })()}
                      </div>
                    ) : (
                      <span className="text-slate-500">Search for a student...</span>
                    )}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[350px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Type to search students..." />
                    <CommandList>
                      <CommandEmpty>No student found.</CommandEmpty>
                      <CommandGroup heading="Students">
                        {students.map((student) => (
                          <CommandItem
                            key={student.id}
                            value={student.name}
                            onSelect={() => {
                              setSelectedStudentId(student.id);
                              setStudentName(student.name);
                              setStudentSearchOpen(false);
                            }}
                            className="cursor-pointer"
                          >
                            <div className="flex items-center justify-between w-full">
                              <div className="flex items-center gap-2">
                                <Check
                                  className={cn(
                                    "h-4 w-4",
                                    selectedStudentId === student.id ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                <span className="font-medium">{student.name}</span>
                              </div>
                              {isAdmin && (() => {
                                const avail = student.available_credits ?? student.remaining_classes;
                                return (
                                  <Badge
                                    variant="outline"
                                    className={cn(
                                      "text-xs ml-2",
                                      avail === 0
                                        ? "bg-red-100 text-red-700 border-red-300 font-semibold"
                                        : avail <= 3
                                        ? "bg-amber-50 text-amber-700 border-amber-200"
                                        : ""
                                    )}
                                  >
                                    {avail} available
                                  </Badge>
                                );
                              })()}
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {selectedStudentId && (
                <p className="text-xs text-green-600">
                  ✓ Student linked - credits will be auto-deducted when tagged
                </p>
              )}
            </div>

            {/* Manual Student Name Input (SECONDARY) */}
            <div className="space-y-2 border-t pt-4">
              <Label className="text-slate-500">Or type student name manually</Label>
              <Input
                placeholder="Enter student name..."
                value={studentName}
                onChange={(e) => {
                  setStudentName(e.target.value);
                  // Clear student ID if manually typing different name
                  const matchingStudent = students.find(s => s.name.toLowerCase() === e.target.value.toLowerCase());
                  if (matchingStudent) {
                    setSelectedStudentId(matchingStudent.id);
                  } else {
                    setSelectedStudentId("");
                  }
                }}
                data-testid="student-name-input"
              />
              {!selectedStudentId && studentName && (
                <p className="text-xs text-amber-600">
                  ⚠️ Manual entry - student not in database, no auto credit deduction
                </p>
              )}
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            {assignDialog.slot?.student_name && (
              <Button
                variant="outline"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={handleUnassignStudent}
                data-testid="unassign-btn"
              >
                <X className="w-4 h-4 mr-2" />
                Remove Student
              </Button>
            )}
            <div className="flex gap-2 ml-auto">
              <Button variant="outline" onClick={() => setAssignDialog({ open: false, slot: null })}>
                Cancel
              </Button>
              <Button
                className="bg-indigo-600 hover:bg-indigo-700"
                onClick={handleAssignStudent}
                disabled={!studentName.trim()}
                data-testid="confirm-assign-btn"
              >
                <User className="w-4 h-4 mr-2" />
                {assignDialog.slot?.student_name ? "Update" : "Book Class"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tag Class Dialog */}
      <Dialog open={tagDialog.open} onOpenChange={(open) => {
        if (!open) {
          setTagDialog({ open: false, slot: null });
          setSelectedStatus("");
          setTagNotes("");
          setEditMode(false);
        }
      }}>
        <DialogContent className="sm:max-w-md" data-testid="tag-dialog">
          <DialogHeader>
            <DialogTitle>{editMode ? "Edit Booking" : "Tag Class Status"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Slot Info Section */}
            <div className="text-sm space-y-2 bg-slate-50 p-3 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-indigo-500" />
                  <span className="font-medium text-slate-800">{tagDialog.slot?.student_name || "No student assigned"}</span>
                  {tagDialog.slot?.student_id && (
                    <Badge variant="outline" className="text-xs bg-green-50 text-green-700">
                      Linked
                    </Badge>
                  )}
                </div>
                {/* Edit Button for Admin */}
                {isAdmin && !editMode && tagDialog.slot?.student_name && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditMode(true)}
                    className="text-indigo-600 hover:text-indigo-700"
                    data-testid="edit-booking-btn"
                  >
                    <Pencil className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-2 text-slate-600">
                <Calendar className="w-4 h-4" />
                {tagDialog.slot?.date}
              </div>
              <div className="flex items-center gap-2 text-slate-600">
                <Clock className="w-4 h-4" />
                {tagDialog.slot?.time && formatTime12h(tagDialog.slot.time)} (Istanbul)
              </div>
              {isAdmin && tagDialog.slot?.tutor_name && (
                <div className="flex items-center gap-2 text-indigo-600">
                  <User className="w-4 h-4" />
                  Tutor: {tagDialog.slot.tutor_name}
                </div>
              )}
            </div>

            {/* Edit Mode: Change Student Assignment */}
            {editMode && isAdmin ? (
              <div className="space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                  <AlertCircle className="w-4 h-4 inline mr-2" />
                  Change the student assigned to this class slot.
                </div>
                
                <div className="space-y-2">
                  <Label className="font-medium">Select Student from Database</Label>
                  <Popover open={studentSearchOpen} onOpenChange={setStudentSearchOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={studentSearchOpen}
                        className="w-full justify-between"
                        data-testid="edit-student-selector"
                      >
                        {selectedStudentId
                          ? students.find(s => s.id === selectedStudentId)?.name
                          : "Search students..."}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0">
                      <Command>
                        <CommandInput placeholder="Search students..." />
                        <CommandList>
                          <CommandEmpty>No students found.</CommandEmpty>
                          <CommandGroup>
                            {students.map((student) => (
                              <CommandItem
                                key={student.id}
                                value={student.name}
                                onSelect={() => {
                                  setSelectedStudentId(student.id);
                                  setStudentName(student.name);
                                  setStudentSearchOpen(false);
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    selectedStudentId === student.id ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                {student.name}
                                <span className={cn(
                                  "ml-2 text-xs",
                                  (student.remaining_classes ?? 0) === 0
                                    ? "text-red-600 font-semibold"
                                    : "text-slate-400"
                                )}>
                                  ({student.remaining_classes ?? 0} credits)
                                </span>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="text-center text-slate-400 text-sm">— OR —</div>

                <div className="space-y-2">
                  <Label className="font-medium">Enter Student Name Manually</Label>
                  <Input
                    placeholder="Type student name..."
                    value={studentName}
                    onChange={(e) => {
                      setStudentName(e.target.value);
                      setSelectedStudentId(""); // Clear selected if typing manually
                    }}
                    data-testid="edit-student-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Notes (Optional)</Label>
                  <Textarea
                    placeholder="Add notes about this change..."
                    value={tagNotes}
                    onChange={(e) => setTagNotes(e.target.value)}
                  />
                </div>
              </div>
            ) : (
              /* Normal Tag Mode: Select Status */
              <>
                <div className="space-y-2">
                  <Label className="font-medium">Class Status *</Label>
                  <div className="grid grid-cols-1 gap-2">
                    <Button
                      type="button"
                      variant={selectedStatus === "present" ? "default" : "outline"}
                      className={`justify-start h-12 ${selectedStatus === "present" ? "bg-purple-400 hover:bg-purple-500" : ""}`}
                      onClick={() => setSelectedStatus("present")}
                      data-testid="status-present"
                    >
                      <div className="w-3 h-3 rounded-full bg-purple-400 mr-3"></div>
                      <div className="text-left">
                        <div className="font-medium">Present</div>
                        <div className="text-xs opacity-75">Student attended - Deducts 1 credit</div>
                      </div>
                    </Button>
                    <Button
                      type="button"
                      variant={selectedStatus === "no_show" ? "default" : "outline"}
                      className={`justify-start h-12 ${selectedStatus === "no_show" ? "bg-amber-500 hover:bg-amber-600" : ""}`}
                      onClick={() => setSelectedStatus("no_show")}
                      data-testid="status-no-show"
                    >
                  <div className="w-3 h-3 rounded-full bg-amber-400 mr-3"></div>
                  <div className="text-left">
                    <div className="font-medium">No Show</div>
                    <div className="text-xs opacity-75">Student didn't show - Deducts 1 credit</div>
                  </div>
                </Button>
                <Button
                  type="button"
                  variant={selectedStatus === "cancelled_by_teacher" ? "default" : "outline"}
                  className={`justify-start h-12 ${selectedStatus === "cancelled_by_teacher" ? "bg-red-400 hover:bg-red-500" : ""}`}
                  onClick={() => setSelectedStatus("cancelled_by_teacher")}
                  data-testid="status-cancelled-by-teacher"
                >
                  <div className="w-3 h-3 rounded-full bg-red-400 mr-3"></div>
                  <div className="text-left">
                    <div className="font-medium">Cancelled by Teacher</div>
                    <div className="text-xs opacity-75">Teacher cancelled - No credit deduction</div>
                  </div>
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea
                placeholder="Add any notes about this class..."
                value={tagNotes}
                onChange={(e) => setTagNotes(e.target.value)}
                data-testid="tag-notes"
              />
            </div>

            {/* Credit deduction warnings */}
            {tagDialog.slot?.credit_deducted && (selectedStatus === "cancelled" || selectedStatus === "cancelled_by_teacher" || selectedStatus === "cancelled_by_student") && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                <AlertCircle className="w-4 h-4 inline mr-2" />
                Changing to Cancelled will refund 1 credit to the student.
              </div>
            )}

            {!tagDialog.slot?.credit_deducted && tagDialog.slot?.student_id && 
             (selectedStatus === "present" || selectedStatus === "no_show") && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                <AlertCircle className="w-4 h-4 inline mr-2" />
                This will deduct 1 credit from {tagDialog.slot?.student_name}'s balance.
              </div>
            )}

            {!tagDialog.slot?.student_id && (selectedStatus === "present" || selectedStatus === "no_show") && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                <AlertCircle className="w-4 h-4 inline mr-2" />
                Student not linked to database - no automatic credit deduction will occur.
              </div>
            )}
              </>
            )}
          </div>
          <DialogFooter>
            {editMode ? (
              <>
                <Button variant="outline" onClick={() => setEditMode(false)}>
                  Back
                </Button>
                <Button
                  className="bg-indigo-600 hover:bg-indigo-700"
                  onClick={handleEditBooking}
                  disabled={!studentName && !selectedStudentId}
                  data-testid="save-edit-btn"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Save Changes
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setTagDialog({ open: false, slot: null })}>
                  Cancel
                </Button>
                <Button
                  className="bg-indigo-600 hover:bg-indigo-700"
                  onClick={handleTagSlot}
                  disabled={!selectedStatus}
                  data-testid="confirm-tag-btn"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Update Status
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
